import { Link } from 'react-router-dom';
import { buttonClasses } from '@/components/ui/button-styles';
import { HERO_TRUST } from '@/content/site-content';
import { HERO_IMAGE } from '@/content/images';
import { ArrowRightIcon, SearchIcon } from '@/components/ui/Icons';

/**
 * The hero: a centred headline over a faint orbit of concentric rings, with the
 * app mock-up and a photograph overlapping below it.
 *
 * The rings and the little emoji nodes on them are decorative — they are drawn
 * in CSS/SVG rather than shipped as an image so they stay crisp and cost
 * nothing to download.
 */

/**
 * The emoji nodes scattered around the rings. Positions are percentages, kept
 * clear of the centre column so nothing ever lands on the headline.
 */
const ORBIT_NODES = [
  { emoji: '📚', top: '9%', left: '4%' },
  { emoji: '🌳', top: '7%', left: '90%' },
  { emoji: '🚜', top: '24%', left: '0%' },
  { emoji: '🐕', top: '26%', left: '94%' },
  { emoji: '🌱', top: '44%', left: '2%' },
  { emoji: '🧹', top: '41%', left: '80%' },
  { emoji: '🎉', top: '46%', left: '93%' },
] as const;

function OrbitRings() {
  return (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      <svg
        viewBox="0 0 1200 900"
        preserveAspectRatio="xMidYMin slice"
        className="absolute top-2 left-1/2 h-[120%] w-[140%] -translate-x-1/2 text-brand-200/50"
      >
        {[190, 300, 410, 520, 630].map((r) => (
          <ellipse
            key={r}
            cx="600"
            cy="400"
            rx={r}
            ry={r * 0.62}
            fill="none"
            stroke="currentColor"
            strokeWidth="1"
          />
        ))}
      </svg>

      {ORBIT_NODES.map((node) => (
        <span
          key={node.emoji}
          style={{ top: node.top, left: node.left }}
          className="absolute hidden size-14 place-items-center rounded-full bg-white text-2xl shadow-soft ring-1 ring-line lg:grid"
        >
          {node.emoji}
        </span>
      ))}
    </div>
  );
}

/** A flat mock-up of the teen home screen, drawn in markup. */
function PhoneMock() {
  const listings = [
    { title: 'Dog Walker Needed', meta: 'Weekends · Near You', pay: '$15/hr', emoji: '🐕' },
    { title: 'Tutoring Assistant', meta: 'Afternoons · 2–3 mi', pay: '$20/hr', emoji: '📚' },
    { title: 'Event Helper', meta: 'One-time · 5 mi', pay: '$18/hr', emoji: '🎈' },
  ];

  return (
    <div
      aria-hidden="true"
      className="w-44 shrink-0 rotate-[-6deg] rounded-[2.25rem] bg-accent-900 p-2.5 shadow-lift sm:w-64"
    >
      <div className="rounded-[1.8rem] bg-accent-900 px-3.5 pt-3 pb-2.5 text-white">
        <div className="flex items-center justify-between text-[0.55rem] font-semibold text-white/70">
          <span>9:41</span>
          <span>▮▮ ▲ ▰</span>
        </div>

        <div className="mt-3 flex items-start justify-between">
          <div>
            <p className="text-sm font-extrabold">Hi, Alex!</p>
            <p className="text-[0.55rem] text-white/60">Ready to find your next opportunity?</p>
          </div>
          <span className="grid size-6 place-items-center rounded-lg bg-brand-600 text-[0.6rem]">
            🔔
          </span>
        </div>

        <div className="mt-3 flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1.5">
          <SearchIcon className="size-2.5 text-white/60" />
          <span className="text-[0.5rem] text-white/60">Search for jobs or skills</span>
        </div>

        <div className="mt-3 flex items-center justify-between text-[0.55rem] font-semibold">
          <span>Recommended for you</span>
          <span className="text-white/50">View all ›</span>
        </div>

        <ul className="mt-1.5 space-y-1.5">
          {listings.map((job) => (
            <li key={job.title} className="flex items-center gap-2 rounded-xl bg-white p-1.5">
              <span className="grid size-7 shrink-0 place-items-center rounded-lg bg-brand-50 text-xs">
                {job.emoji}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[0.58rem] font-bold text-ink">
                  {job.title}
                </span>
                <span className="block truncate text-[0.48rem] text-ink-soft">{job.meta}</span>
                <span className="block text-[0.52rem] font-bold text-brand-600">{job.pay}</span>
              </span>
            </li>
          ))}
        </ul>

        <div className="mt-3 flex justify-around border-t border-white/10 pt-2 text-[0.42rem]">
          {['Home', 'My Jobs', 'Messages', 'Profile'].map((tab, index) => (
            <span key={tab} className={index === 0 ? 'font-bold text-brand-400' : 'text-white/45'}>
              {tab}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-white pt-10 pb-0 sm:pt-14">
      <OrbitRings />

      <div className="container-page relative">
        <h1 className="mx-auto max-w-4xl text-center text-4xl leading-[1.12] font-extrabold sm:text-5xl lg:text-[3.55rem]">
          Opportunities. Growth.
          <br />
          <span className="relative inline-block">
            Earnings.
            {/* The hand-drawn underline under "Earnings". */}
            <svg
              aria-hidden="true"
              viewBox="0 0 220 12"
              preserveAspectRatio="none"
              className="absolute -bottom-1 left-0 h-2.5 w-full text-ink"
            >
              <path
                d="M2 8C40 3 90 2 150 5c25 1.2 50 3 68 4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            </svg>
          </span>{' '}
          <span className="text-brand-600">Your Future.</span>
        </h1>

        <p className="mx-auto mt-6 max-w-2xl text-center text-base leading-relaxed text-ink-soft sm:text-lg">
          The trusted marketplace where teens, young adults, and young entrepreneurs discover
          opportunities, build skills, earn, and grow with confidence.
        </p>

        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Link to="/register" className={buttonClasses('secondary', 'md', 'rounded-full')}>
            I&rsquo;m a Teen (Ages 14–17)
            <ArrowRightIcon className="size-4" />
          </Link>
          <Link to="/register" className={buttonClasses('outline', 'md', 'rounded-full')}>
            I&rsquo;m Teen+ (Ages 18–21)
            <ArrowRightIcon className="size-4" />
          </Link>
        </div>

        <p className="mt-4 text-center text-xs text-ink-soft">
          You&rsquo;ll confirm your age before creating a profile.
        </p>

        <ul className="mt-6 flex flex-wrap items-center justify-center gap-x-7 gap-y-3">
          {HERO_TRUST.map((item) => (
            <li key={item.label} className="flex items-center gap-2 text-sm font-medium text-ink">
              <item.icon className="size-4 shrink-0 text-brand-600" />
              {item.label}
            </li>
          ))}
        </ul>

        {/* Phone mock-up overlapping the photograph. */}
        <div className="mt-12 flex items-center justify-center pb-10 sm:mt-16">
          <PhoneMock />
          <img
            src={HERO_IMAGE.src}
            alt={HERO_IMAGE.alt}
            width={HERO_IMAGE.width}
            height={HERO_IMAGE.height}
            // The hero is above the fold, so it loads eagerly and gets priority.
            fetchPriority="high"
            decoding="async"
            className="-ml-8 aspect-square w-36 rounded-[1.75rem] object-cover shadow-lift sm:-ml-10 sm:w-64 lg:w-72"
          />
        </div>
      </div>
    </section>
  );
}
